import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { TokenManager } from '../../services/api';
import authApi from '../../services/auth';

/**
 * 认证状态管理 Slice
 * 支持登录、注册、获取用户信息、登出等操作
 */

// ==================== 异步 Thunks ====================

/**
 * 异步登录
 */
export const loginAsync = createAsyncThunk(
  'auth/login',
  async (credentials, { rejectWithValue }) => {
    try {
      const response = await authApi.login(credentials);
      return response.data;
    } catch (error) {
      // 响应拦截器在非200响应时创建的 Error 对象结构：
      // - error.message: 错误消息
      // - error.code: 业务错误码
      // - error.data: 响应中的 data 字段（可能包含 captchaRequired 等信息）
      return rejectWithValue({
        message: error?.message || '登录失败',
        data: error?.data,  // 直接使用 error.data，不是 error.response.data
        code: error?.code,
      });
    }
  }
);

/**
 * 异步注册
 */
export const registerAsync = createAsyncThunk(
  'auth/register',
  async (userData, { rejectWithValue }) => {
    try {
      const response = await authApi.register(userData);
      return response.data;
    } catch (error) {
      return rejectWithValue(error?.response?.data?.message || error?.message || '注册失败');
    }
  }
);

/**
 * 异步获取用户信息
 */
export const getUserInfoAsync = createAsyncThunk(
  'auth/getUserInfo',
  async (_, { rejectWithValue }) => {
    try {
      const response = await authApi.getUserInfo();
      return response.data;
    } catch (error) {
      return rejectWithValue(error?.response?.data?.message || error?.message || '获取用户信息失败');
    }
  }
);

/**
 * 异步登出
 */
export const logoutAsync = createAsyncThunk(
  'auth/logout',
  async (_, { rejectWithValue }) => {
    try {
      await authApi.logout();
      return true;
    } catch (error) {
      // 即使后端调用失败，也要清除本地状态
      console.error('退出登录接口调用失败:', error);
      return true;
    }
  }
);

// ==================== Slice ====================

const initialState = {
  // 是否已登录
  isAuthenticated: false,
  // 用户信息
  user: null,
  // 访问 token
  token: null,
  // 权限列表
  permissions: [],
  // 角色列表
  roles: [],
  // 加载状态
  loading: false,
  // 初始化状态
  initialized: false,
  // 错误信息
  error: null,
};

const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    // 登出（同步方式，清除登录状态和Token）
    logout: (state) => {
      state.isAuthenticated = false;
      state.token = null;
      state.user = null;
      state.permissions = [];
      state.roles = [];
      state.error = null;
      // 清除 Token（同时清除 sessionStorage 和 localStorage）
      TokenManager.clearToken();
    },
    // 清除错误信息
    clearError: (state) => {
      state.error = null;
    },
    // 设置权限列表
    setPermissions: (state, action) => {
      state.permissions = action.payload;
    },
    // 设置角色列表
    setRoles: (state, action) => {
      state.roles = action.payload;
    },
    // 更新用户权限和角色
    updateUserPermissions: (state, action) => {
      state.permissions = action.payload.permissions || [];
      state.roles = action.payload.roles || [];
    },
    // 从 storage 恢复 token（用于应用初始化）
    restoreToken: (state) => {
      const token = TokenManager.getToken();
      if (token) {
        state.token = token;
        state.isAuthenticated = true;
      }
    },
  },
  extraReducers: (builder) => {
    builder
      // 登录
      .addCase(loginAsync.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(loginAsync.fulfilled, (state, action) => {
        state.loading = false;
        state.initialized = true;
        state.isAuthenticated = true;
        state.user = action.payload.user || action.payload.userInfo;
        state.token = action.payload.token || null;
        state.permissions = action.payload.permissions || [];
        state.roles = action.payload.roles || [];
        state.error = null;
        // 使用 TokenManager 保存 token（同时设置到 sessionStorage 和 localStorage）
        if (action.payload.token) {
          TokenManager.setToken(action.payload.token);
        }
      })
      .addCase(loginAsync.rejected, (state, action) => {
        state.loading = false;
        state.initialized = true;
        state.error = action.payload;
      })
      // 注册
      .addCase(registerAsync.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(registerAsync.fulfilled, (state) => {
        state.loading = false;
        state.initialized = true;
        state.error = null;
      })
      .addCase(registerAsync.rejected, (state, action) => {
        state.loading = false;
        state.initialized = true;
        state.error = action.payload;
      })
      // 获取用户信息
      .addCase(getUserInfoAsync.pending, (state) => {
        state.loading = true;
      })
      .addCase(getUserInfoAsync.fulfilled, (state, action) => {
        state.loading = false;
        state.initialized = true;
        state.isAuthenticated = true;
        state.user = action.payload.user || action.payload.userInfo || action.payload;
        state.permissions = action.payload.permissions || [];
        state.roles = action.payload.roles || [];
      })
      .addCase(getUserInfoAsync.rejected, (state, action) => {
        state.loading = false;
        state.initialized = true;
        state.isAuthenticated = false;
        state.user = null;
        state.token = null;
        state.permissions = [];
        state.roles = [];
        state.error = action.payload;
      })
      // 登出
      .addCase(logoutAsync.fulfilled, (state) => {
        // 使用 logout action 清除状态和Token
        authSlice.caseReducers.logout(state);
      });
  },
});

export const { logout, clearError, setPermissions, setRoles, updateUserPermissions, restoreToken } = authSlice.actions;
export default authSlice.reducer;

// ==================== 选择器 ====================

export const selectIsAuthenticated = (state) => state.auth.isAuthenticated;
export const selectUser = (state) => state.auth.user;
export const selectToken = (state) => state.auth.token;
export const selectAuthLoading = (state) => state.auth.loading;
export const selectAuthError = (state) => state.auth.error;
export const selectAuthInitialized = (state) => state.auth.initialized;
export const selectPermissions = (state) => state.auth.permissions;
export const selectRoles = (state) => state.auth.roles;
